import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { createPageUrl } from "@/utils";
import { hapticLight } from "@/utils/haptic";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

import navSkullOrange from "@/assets/nav-skull-orange.png";
import navSkullGreen from "@/assets/nav-skull-green.png";
import navSkullRed from "@/assets/nav-skull-red.png";
import navSkullYellow from "@/assets/nav-skull-yellow.png";
import navSkullPink from "@/assets/nav-skull-pink.png";
import navSkullCyan from "@/assets/nav-skull-cyan.png";
import navSkullBlueHeadphones from "@/assets/nav-skull-blue-headphones.png";
import navSkullZebra from "@/assets/nav-skull-zebra.png";

const navItems = [
  { icon: navSkullOrange, label: "Discover", path: createPageUrl("Discover"), glowColor: "255,140,0" },
  { icon: navSkullGreen, label: "Submit", path: createPageUrl("SubmitOptions"), glowColor: "0,255,0" },
  { icon: navSkullCyan, label: "New Tracks", path: createPageUrl("FreshDopeSounds"), glowColor: "0,255,255" },
  { icon: navSkullZebra, label: "Tools", path: createPageUrl("Tools"), glowColor: "200,200,200" },
  { icon: navSkullRed, label: "Leaderboard", path: createPageUrl("Leaderboard"), glowColor: "255,0,0" },
  { icon: navSkullYellow, label: "Upload", path: createPageUrl("Upload"), glowColor: "255,255,0" },
  { icon: navSkullPink, label: "Profile", path: createPageUrl("Profile"), glowColor: "255,0,255" },
];

export default function MobileSidebar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="fixed bottom-4 right-4 z-[10001] w-14 h-14 rounded-full neuro-base flex items-center justify-center md:hidden landscape:hidden"
          style={{
            background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)',
            paddingBottom: 'env(safe-area-inset-bottom, 0)'
          }}
          onClick={() => hapticLight()}
          aria-label="Open navigation menu"
        >
          <Menu className="w-6 h-6 text-[#d0d0d0]" />
        </button>
      </SheetTrigger>
      <SheetContent 
        side="right" 
        className="w-[280px] bg-[#0a0a0a] border-l border-[#1f1f1f] p-0 overflow-y-auto"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 20px)' }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#1f1f1f]">
            <span className="text-lg font-bold text-[#d0d0d0] font-holly-berry tracking-wider">
              MENU
            </span>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 p-3 space-y-2">
            {navItems.map(({ icon, label, path, glowColor }) => (
              <Link
                key={path}
                to={path}
                onClick={() => {
                  hapticLight();
                  setOpen(false);
                }}
                className={`flex items-center gap-4 p-3 rounded-xl transition-all duration-200 ${
                  isActive(path) 
                    ? 'bg-[#1a1a1a] ring-1 ring-[#2a2a2a]' 
                    : 'hover:bg-[#151515]'
                }`}
                style={{
                  boxShadow: isActive(path) 
                    ? `0 0 15px rgba(${glowColor}, 0.3)` 
                    : 'none',
                }}
              >
                <div className="w-10 h-10 flex-shrink-0">
                  <img
                    src={icon}
                    alt={label}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <span 
                  className={`text-base font-bold font-holly-berry tracking-wide ${
                    isActive(path) ? 'text-white' : 'text-[#a0a0a0]'
                  }`}
                  style={{ transform: 'scaleY(1.2)', transformOrigin: 'center' }}
                >
                  {label}
                </span>
              </Link>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-[#1f1f1f] text-center">
            <span className="text-xs text-[#505050]">Swipe or tap outside to close</span>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
